import React, { useMemo, useState } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import styled from 'styled-components';
import { ANALYTIC_PANEL_COLORS } from 'utils/constants/analyticPanelColors';
import { stringToNumberOrZero } from 'utils/formatters/stringToNumberOrZero';
import { ReportData, ShareItem } from 'utils/types/AnalyticPanelReport';

import { PDF_EXPORT_CLASS } from './pdfExportClass';

const SectionTitle = styled.h2`
    font-size: 16px;
    font-weight: 600;
    color: var(--color-primary);
    margin: 0 0 24px;
`;

const TwoColumnGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    margin-bottom: 20px;

    @media (max-width: 992px) {
        grid-template-columns: 1fr;
    }

    .${PDF_EXPORT_CLASS} & {
        grid-template-columns: 1fr 1fr;
    }
`;

const ChartCard = styled.div`
    background: var(--color-white);
    border-radius: var(--box-border-radius);
    padding: 24px;

    .${PDF_EXPORT_CLASS} & {
        border: 1px solid var(--pdf-card-border);
    }
`;

const ChartColumn = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const ChartSubtitle = styled.div`
    font-size: 11px;
    text-transform: uppercase;
    font-weight: 600;
    letter-spacing: 0.5px;
    color: var(--color-primary-70);
    margin-bottom: 16px;
`;

const PieChartContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 180px;
`;

const LegendList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 16px 0 0;
    width: 100%;
`;

const LegendItem = styled.li<{ $isActive?: boolean }>`
    display: flex;
    align-items: center;
    padding: 8px 12px;
    font-size: 13px;
    border-radius: 6px;
    margin-right: 18px;
    background: ${({ $isActive }) => $isActive ? 'var(--color-alt-second)' : 'transparent'};
    font-weight: ${({ $isActive }) => $isActive ? 600 : 400};
    transition: background 0.2s ease;
`;

const ColorDot = styled.span<{ $color: string }>`
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${({ $color }) => $color};
    margin-right: 8px;
    flex-shrink: 0;
`;

const LegendLabel = styled.span`
    flex: 1;
`;

const LegendValue = styled.span`
    font-weight: 600;
    color: var(--color-primary);
    margin-right: 16px;
`;

const LegendPercent = styled.span`
    font-weight: 600;
    color: var(--color-primary-70);
    min-width: 50px;
    text-align: right;
`;

interface StructureSectionProps {
    data: ReportData;
    isAnimationActive?: boolean;
}

const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pl-PL', {
        style: 'decimal',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(value) + ' zł';
};

export const StructureSection: React.FC<StructureSectionProps> = ({ data, isAnimationActive = true }) => {
    const { calculation } = data;
    const [activeProfitIndex, setActiveProfitIndex] = useState<number | null>(null);
    const [activeCapitalIndex, setActiveCapitalIndex] = useState<number | null>(null);

    const profitData: ShareItem[] = useMemo(() => {
        if (!calculation) return [];

        return [
            {
                label: 'Zysk Flippera',
                percent: stringToNumberOrZero(calculation.flipperProfitPercent),
                value: stringToNumberOrZero(calculation.flipperProfitAmount),
                color: ANALYTIC_PANEL_COLORS.saleCosts
            },
            {
                label: 'Zysk Inwestora',
                percent: stringToNumberOrZero(calculation.investorProfitPercent),
                value: stringToNumberOrZero(calculation.investorProfitAmount),
                color: ANALYTIC_PANEL_COLORS.purchaseCosts
            }
        ];
    }, [calculation]);

    const capitalData: ShareItem[] = useMemo(() => {
        if (!calculation) return [];

        return [
            {
                label: 'Kapitał Flippera',
                percent: stringToNumberOrZero(calculation.flipperCapitalPercent),
                value: stringToNumberOrZero(calculation.flipperCapitalAmount),
                color: ANALYTIC_PANEL_COLORS.purchasePrice
            },
            {
                label: 'Kapitał Inwestora',
                percent: stringToNumberOrZero(calculation.investorCapitalPercent),
                value: stringToNumberOrZero(calculation.investorCapitalAmount),
                color: ANALYTIC_PANEL_COLORS.renovation
            }
        ];
    }, [calculation]);

    const profitPieData = profitData.map((item) => ({
        name: item.label,
        value: item.value,
        percentage: `${item.percent}%`,
        color: item.color
    }));

    const capitalPieData = capitalData.map((item) => ({
        name: item.label,
        value: item.value,
        percentage: `${item.percent}%`,
        color: item.color
    }));

    if (!calculation) return null;

    return (
        <>
            <SectionTitle>Podział Kapitału i Zysku</SectionTitle>
            <TwoColumnGrid>
                <ChartCard>
                    <ChartColumn>
                        <ChartSubtitle>Struktura Zysku</ChartSubtitle>
                        <PieChartContainer>
                            <ResponsiveContainer width="100%" height={160}>
                                <PieChart>
                                    <Pie
                                        isAnimationActive={isAnimationActive}
                                        data={profitPieData}
                                        dataKey="value"
                                        nameKey="name"
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={70}
                                        innerRadius={35}
                                        stroke="none"
                                        onMouseEnter={(_, index) => setActiveProfitIndex(index)}
                                        onMouseLeave={() => setActiveProfitIndex(null)}
                                    >
                                        {profitPieData.map((entry, index) => <Cell
                                            key={`profit-cell-${index}`}
                                            fill={entry.color}
                                            strokeWidth={activeProfitIndex === index ? 3 : 0}
                                            stroke="white"
                                        />)}
                                    </Pie>
                                    <Tooltip formatter={(value: number) => formatCurrency(value)} />
                                </PieChart>
                            </ResponsiveContainer>
                        </PieChartContainer>
                        <LegendList>
                            {profitData.map((item, index) => <LegendItem
                                key={item.label}
                                $isActive={activeProfitIndex === index}
                                onMouseEnter={() => setActiveProfitIndex(index)}
                                onMouseLeave={() => setActiveProfitIndex(null)}
                            >
                                <ColorDot $color={item.color} />
                                <LegendLabel>{item.label}</LegendLabel>
                                <LegendValue>{formatCurrency(item.value)}</LegendValue>
                                <LegendPercent>{item.percent}%</LegendPercent>
                            </LegendItem>)}
                        </LegendList>
                    </ChartColumn>
                </ChartCard>

                <ChartCard>
                    <ChartColumn>
                        <ChartSubtitle>Struktura Kapitału</ChartSubtitle>
                        <PieChartContainer>
                            <ResponsiveContainer width="100%" height={160}>
                                <PieChart>
                                    <Pie
                                        isAnimationActive={isAnimationActive}
                                        data={capitalPieData}
                                        dataKey="value"
                                        nameKey="name"
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={70}
                                        innerRadius={35}
                                        stroke="none"
                                        onMouseEnter={(_, index) => setActiveCapitalIndex(index)}
                                        onMouseLeave={() => setActiveCapitalIndex(null)}
                                    >
                                        {capitalPieData.map((entry, index) => <Cell
                                            key={`capital-cell-${index}`}
                                            fill={entry.color}
                                            strokeWidth={activeCapitalIndex === index ? 3 : 0}
                                            stroke="white"
                                        />)}
                                    </Pie>
                                    <Tooltip formatter={(value: number) => formatCurrency(value)} />
                                </PieChart>
                            </ResponsiveContainer>
                        </PieChartContainer>
                        <LegendList>
                            {capitalData.map((item, index) => <LegendItem
                                key={item.label}
                                $isActive={activeCapitalIndex === index}
                                onMouseEnter={() => setActiveCapitalIndex(index)}
                                onMouseLeave={() => setActiveCapitalIndex(null)}
                            >
                                <ColorDot $color={item.color} />
                                <LegendLabel>{item.label}</LegendLabel>
                                <LegendValue>{formatCurrency(item.value)}</LegendValue>
                                <LegendPercent>{item.percent}%</LegendPercent>
                            </LegendItem>)}
                        </LegendList>
                    </ChartColumn>
                </ChartCard>
            </TwoColumnGrid>
        </>
    );
};
